import { useMutation } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';
import { apiUrlGenerator } from 'utils/useTable';
import { ccmPageAvailableStatuses } from 'constants/patient_statuses';
import forms from 'constants/forms';

interface Props {
  searchKey: string;
  searchValue: string;
}
interface IItem {
  guid: string;
  firstName: string;
  lastName: string;
  birthDate: string;
}
interface IResponse {
  count: number;
  items: IItem[];
}

const excludeOptions = [
  'firstName',
  'lastName',
  'birthDate',
  'insuranceNumber',
];

export const options = forms.patientFilterOptions
  .filter((opt) => !excludeOptions.includes(opt.id))
  .map((opt) => ({
    value: opt.id,
    label: opt.label,
    placeholder: opt.placeholder,
  }));

const ccmStatus = ccmPageAvailableStatuses.join(',');

const generateFilterLabel = (item: IItem) =>
  `${item.firstName} ${item.lastName} - ${item.birthDate}`;

const searchPatients = async ({ searchKey, searchValue }: Props) => {
  const url = 'core/patients';
  const items = 100;
  const page = 1;
  const filters = {
    [searchKey]: searchValue.trim(),
    patientRequestType: 'search',
    status: 'active_onboarding_inactive',
    ccmStatus,
  };

  const { data, error } = await dataService.getList<IResponse>(
    apiUrlGenerator(url, items, page, filters),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return (
    data?.items.map((item) => ({
      value: item.guid,
      label: generateFilterLabel(item),
    })) ?? []
  );
};

export const useSearchPatient = () => {
  const { mutate, data, isLoading } = useMutation({
    mutationFn: ({ searchKey, searchValue }: Props) =>
      searchPatients({ searchKey, searchValue }),
  });
  return { search: mutate, dataOptions: data, isLoading };
};
